import { useState } from 'react';
import { useRouter } from 'next/router';
import { useAuth } from '@c/../context/AuthContext';
import { useToastContext } from '@c/../context/ToastContext';
import { MasterDocument, ReviewLink, YouTubeVideo } from '@util/firestore/master/master.types';

interface AdminEditPanelProps {
  master: MasterDocument;
}

type TabName = 'image' | 'reviews' | 'videos' | 'faq';

const TABS: { key: TabName; label: string }[] = [
  { key: 'image', label: 'Image' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'videos', label: 'Videos' },
  { key: 'faq', label: 'FAQ' },
];

const MAX_FILE_SIZE = 1.5 * 1024 * 1024; // 1.5MB

export default function AdminEditPanel({ master }: AdminEditPanelProps) {
  const { userDoc } = useAuth();
  const { showSuccessToast, showErrorToast } = useToastContext();
  const router = useRouter();

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabName>('image');
  // Known limitation: shared loading state across all tabs
  const [loading, setLoading] = useState(false);

  // Image tab state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Reviews tab state
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewUrl, setReviewUrl] = useState('');
  const [reviewSource, setReviewSource] = useState('');
  const [reviewDate, setReviewDate] = useState('');

  // Videos tab state
  const [videoId, setVideoId] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [videoChannel, setVideoChannel] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoDate, setVideoDate] = useState('');
  const [videoFeatured, setVideoFeatured] = useState(false);

  // FAQ tab state
  const [faqQuestion, setFaqQuestion] = useState('');
  const [faqAnswer, setFaqAnswer] = useState('');

  if (!userDoc?.roles?.admin) return null;

  const handleApiCall = async (body: Record<string, unknown>) => {
    setLoading(true);
    try {
      const res = await fetch('/api/master/admin-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Request failed');
      }
      showSuccessToast('Updated successfully');
      router.replace(router.asPath);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong';
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSubmit = async () => {
    if (!selectedFile) {
      showErrorToast('Please select a file');
      return;
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      showErrorToast('File exceeds 1.5MB limit');
      return;
    }
    const base64 = await fileToBase64(selectedFile);
    await handleApiCall({
      operation: 'update-image',
      masterId: master.id,
      imageBase64: base64,
      fileName: selectedFile.name,
      contentType: selectedFile.type,
    });
    setSelectedFile(null);
    // Reset the file input
    const fileInput = document.getElementById('admin-image-upload') as HTMLInputElement | null;
    if (fileInput) fileInput.value = '';
  };

  const handleReviewSubmit = async () => {
    if (!reviewTitle.trim() || !reviewUrl.trim() || !reviewSource.trim()) {
      showErrorToast('Title, URL, and Source are required');
      return;
    }
    await handleApiCall({
      operation: 'add-review',
      masterId: master.id,
      review: {
        title: reviewTitle.trim(),
        url: reviewUrl.trim(),
        source: reviewSource.trim(),
        date: reviewDate.trim() || undefined,
      },
    });
    setReviewTitle('');
    setReviewUrl('');
    setReviewSource('');
    setReviewDate('');
  };

  const handleVideoSubmit = async () => {
    if (!videoId.trim() || !videoTitle.trim() || !videoChannel.trim() || !videoUrl.trim()) {
      showErrorToast('Video ID, Title, Channel, and URL are required');
      return;
    }
    await handleApiCall({
      operation: 'add-video',
      masterId: master.id,
      video: {
        videoId: videoId.trim(),
        title: videoTitle.trim(),
        channel: videoChannel.trim(),
        url: videoUrl.trim(),
        date: videoDate.trim() || undefined,
        featured: videoFeatured,
      },
    });
    setVideoId('');
    setVideoTitle('');
    setVideoChannel('');
    setVideoUrl('');
    setVideoDate('');
    setVideoFeatured(false);
  };

  const handleFaqSubmit = async () => {
    if (!faqQuestion.trim() || !faqAnswer.trim()) {
      showErrorToast('Question and Answer are required');
      return;
    }
    await handleApiCall({
      operation: 'add-faq',
      masterId: master.id,
      faq: {
        question: faqQuestion.trim(),
        answer: faqAnswer.trim(),
      },
    });
    setFaqQuestion('');
    setFaqAnswer('');
  };

  const thumbnailSrc = master.thumbnail || master.images?.[0];

  return (
    <>
      {/* Floating edit button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-overlay bg-brand-secondary text-white p-3 rounded-full shadow-lg hover:opacity-90 transition-opacity"
        aria-label="Open admin edit panel"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
        </svg>
      </button>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-modal bg-black/30"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 z-modal h-full w-[400px] bg-white shadow-xl transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h2 className="text-lg font-semibold">Admin Edit</h2>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-500 hover:text-gray-800 p-1"
            aria-label="Close panel"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-2 text-sm font-medium text-center transition-colors ${
                activeTab === tab.key
                  ? 'text-brand-secondary border-b-2 border-brand-secondary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="p-4 overflow-y-auto" style={{ height: 'calc(100% - 105px)' }}>
          {/* Image Tab */}
          {activeTab === 'image' && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Current Thumbnail
                </label>
                {thumbnailSrc ? (
                  <img
                    src={thumbnailSrc}
                    alt={master.title || 'Product thumbnail'}
                    className="w-full max-w-[200px] h-auto rounded border"
                  />
                ) : (
                  <p className="text-sm text-gray-400">No thumbnail set</p>
                )}
              </div>
              <div>
                <label
                  htmlFor="admin-image-upload"
                  className="text-sm font-medium text-gray-700 block mb-1"
                >
                  Upload New Image
                </label>
                <input
                  id="admin-image-upload"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    if (file && file.size > MAX_FILE_SIZE) {
                      showErrorToast('File exceeds 1.5MB limit');
                      e.target.value = '';
                      setSelectedFile(null);
                      return;
                    }
                    setSelectedFile(file);
                  }}
                  className="border rounded px-3 py-2 w-full text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Max file size: 1.5MB</p>
              </div>
              <button
                onClick={handleImageSubmit}
                disabled={loading || !selectedFile}
                className={`bg-brand-secondary text-white px-4 py-2 rounded ${
                  loading || !selectedFile ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Uploading...' : 'Upload Image'}
              </button>
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Existing Reviews</h3>
                {master.reviews && master.reviews.length > 0 ? (
                  <ul className="space-y-2">
                    {master.reviews.map((review: ReviewLink, idx: number) => (
                      <li
                        key={idx}
                        className="text-sm border rounded p-2 bg-gray-50"
                      >
                        <p className="font-medium">{review.title}</p>
                        <p className="text-gray-500 text-xs">
                          {review.source}
                          {review.date ? ` - ${review.date}` : ''}
                        </p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-400">No reviews yet</p>
                )}
              </div>
              <hr />
              <h3 className="text-sm font-medium text-gray-700">Add Review</h3>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Title *</label>
                <input
                  type="text"
                  value={reviewTitle}
                  onChange={(e) => setReviewTitle(e.target.value)}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="Review title"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">URL *</label>
                <input
                  type="text"
                  value={reviewUrl}
                  onChange={(e) => setReviewUrl(e.target.value)}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Source *</label>
                <input
                  type="text"
                  value={reviewSource}
                  onChange={(e) => setReviewSource(e.target.value)}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="e.g. DPReview"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Date</label>
                <input
                  type="text"
                  value={reviewDate}
                  onChange={(e) => setReviewDate(e.target.value)}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="e.g. 2024-01-15"
                />
              </div>
              <button
                onClick={handleReviewSubmit}
                disabled={loading}
                className={`bg-brand-secondary text-white px-4 py-2 rounded ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Saving...' : 'Add Review'}
              </button>
            </div>
          )}

          {/* Videos Tab */}
          {activeTab === 'videos' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Existing Videos</h3>
                {master.youtube_videos && master.youtube_videos.length > 0 ? (
                  <ul className="space-y-2">
                    {master.youtube_videos.map((video: YouTubeVideo, idx: number) => (
                      <li
                        key={idx}
                        className="text-sm border rounded p-2 bg-gray-50"
                      >
                        <p className="font-medium">
                          {video.title}
                          {video.featured && (
                            <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded">
                              Featured
                            </span>
                          )}
                        </p>
                        <p className="text-gray-500 text-xs">{video.channel}</p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-400">No videos yet</p>
                )}
              </div>
              <hr />
              <h3 className="text-sm font-medium text-gray-700">Add Video</h3>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Video ID *
                </label>
                <input
                  type="text"
                  value={videoId}
                  onChange={(e) => setVideoId(e.target.value)}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="e.g. dQw4w9WgXcQ"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Title *</label>
                <input
                  type="text"
                  value={videoTitle}
                  onChange={(e) => setVideoTitle(e.target.value)}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="Video title"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Channel *</label>
                <input
                  type="text"
                  value={videoChannel}
                  onChange={(e) => setVideoChannel(e.target.value)}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="Channel name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">URL *</label>
                <input
                  type="text"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="https://youtube.com/watch?v=..."
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Date</label>
                <input
                  type="text"
                  value={videoDate}
                  onChange={(e) => setVideoDate(e.target.value)}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="e.g. 2024-01-15"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="video-featured"
                  type="checkbox"
                  checked={videoFeatured}
                  onChange={(e) => setVideoFeatured(e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="video-featured" className="text-sm text-gray-700">
                  Featured video — only one allowed
                </label>
              </div>
              <button
                onClick={handleVideoSubmit}
                disabled={loading}
                className={`bg-brand-secondary text-white px-4 py-2 rounded ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Saving...' : 'Add Video'}
              </button>
            </div>
          )}

          {/* FAQ Tab */}
          {activeTab === 'faq' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Existing FAQs: {master.gmFaq?.length || 0} items
                </h3>
              </div>
              <hr />
              <h3 className="text-sm font-medium text-gray-700">Add FAQ</h3>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Question *
                </label>
                <input
                  type="text"
                  value={faqQuestion}
                  onChange={(e) => setFaqQuestion(e.target.value)}
                  className="border rounded px-3 py-2 w-full"
                  placeholder="Enter question"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Answer *</label>
                <textarea
                  value={faqAnswer}
                  onChange={(e) => setFaqAnswer(e.target.value)}
                  className="border rounded px-3 py-2 w-full"
                  rows={4}
                  placeholder="Enter answer"
                />
              </div>
              <button
                onClick={handleFaqSubmit}
                disabled={loading}
                className={`bg-brand-secondary text-white px-4 py-2 rounded ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Saving...' : 'Add FAQ'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/** Convert a File to a base64 string (data portion only, no prefix) */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Strip the data:...;base64, prefix
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
